import { Col, Row } from 'react-grid-system';
import styled from 'styled-components';
import {
  useErrorHandlerContext,
  useUserLanguage
} from '@/customHooks';
import textsData from '@/textData/createTenantTexts.json'
import { Button, RowSpacer } from '../atoms';
import { useNavigate } from 'react-router';
import { CreateTenantMutation, UpdateTenantMutation, useCreateTenantMutation, UserGenderTypeOption, useUpdateTenantMutation } from '@/graphql/generated';
import { FetchResult } from '@apollo/client';
import { ActivityType, AddressProps, BankDataProps, BillingDataProps, TenantDeatils, UserPersonalDataProps } from '@/custom-types';
import { useCallback, useRef, useState } from 'react';
import { AddressFormControlled } from './AddressFormControlled';
import { UserPersonalDataFormControlled } from './UserPersonalDataFormControlled';
import { BankFormControlled } from './BankFormControlled';
import { BillingFormControlled } from './BillingFormControlled';
import { WarnText } from '../atoms/WarnText';

type CreateTenentFormProps = {
  tenantDetail: TenantDeatils | null
  activity: ActivityType
  readOnly?: boolean
  onSuccess?: (result: FetchResult<CreateTenantMutation | UpdateTenantMutation>) => void
}

type TenantFormData = {
  firstName: string | null,
  lastName: string | null,
  phoneNumber: string | null,
  phoneNumber2: string | null,
  email: string | null,
  documentId: string | null,
  nationality: string | null,
  genderOption: UserGenderTypeOption | null,
  birthDate: string | null,
  street: string | null,
  buildingNumber: string | null,
  addressLine: string | null,
  city: string | null,
  state: string | null,
  country: string | null,
  zipCode: string | null,
  billingLastName: string | null,
  billingFirstName: string | null,
  billingStreet: string | null,
  billingBuildingNumber: string | null,
  billingAddressLine: string | null,
  billingCity: string | null,
  billingCountry: string | null,
  billingState: string | null,
  billingZipCode: string | null,
  billingWereGoing: string | null,
  billingBic: string | null,
  billingObservation: string | null,
  bankAccountName: string | null,
  bankAccountHolder: string | null,
  bankAccountNumber: string | null,
  bankAccountEmail: string | null,
  bankAccountPhone: string | null,
}

const emptyData: TenantFormData = {
  firstName: '',
  lastName: '',
  phoneNumber: '',
  phoneNumber2: '',
  email: '',
  documentId: '',
  nationality: '',
  genderOption: UserGenderTypeOption.Other,
  birthDate: '',
  street: '',
  buildingNumber: '',
  addressLine: '',
  city: '',
  state: '',
  country: '',
  zipCode: '',
  billingLastName: '',
  billingFirstName: '',
  billingStreet: '',
  billingBuildingNumber: '',
  billingAddressLine: '',
  billingCity: '',
  billingCountry: '',
  billingState: '',
  billingZipCode: '',
  billingWereGoing: '',
  billingBic: '',
  billingObservation: '',
  bankAccountName: '',
  bankAccountHolder: '',
  bankAccountNumber: '',
  bankAccountEmail: '',
  bankAccountPhone: '',
}

const StyledForm = styled.form`
  background-color: ${({ theme }) => theme.colors.light};
  padding: 2rem;
`

const SectionWrapper = styled.div`
  border-radius: ${({ theme }) => theme.border.radius.base};
  border-style: solid;
  border-color: ${({ theme }) => theme.colors.border};
  border-width: 1px;
  padding: 2rem;
  margin-top: 1.5rem;
  box-sizing: border-box;
  position: relative;
  margin-bottom: 1.5rem;
  & > span {
    background-color: ${({ theme }) => theme.colors.light};
    position: absolute;
    padding-left: 1rem;
    padding-right: 1rem;
    top: -1.3rem;
    line-height: 0;
  }
`

export const CreateTenantForm = ({ tenantDetail, activity, readOnly, onSuccess }: CreateTenentFormProps) => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const tenantFormDataRef = useRef<TenantFormData>(emptyData)
  const [isWarnTextHidden, setIsWarnTextHidden] = useState(true)

  const navigate = useNavigate()

  const { open: errorHandle } = useErrorHandlerContext()
  
  const [createTenant, { loading: createTenantIsLoading }] = useCreateTenantMutation()
  const [updateTenant, { loading: updateTenantIsLoading }] = useUpdateTenantMutation()

  const onCreateSuccessHandle = (response: FetchResult<CreateTenantMutation>) => {
    const typeName = response.data?.createTenant.__typename;

    if (typeName === 'TenantOutput') onSuccess?.(response)
    else if (typeName === 'ActionResponseMessageOutput') setIsWarnTextHidden(false)

    return
  }

  const onUpdateSuccessHandle = (response: FetchResult<UpdateTenantMutation>) => {
    const typeName = response.data?.updateTenant.__typename;

    if (typeName === 'TenantOutput') onSuccess?.(response)
    else if (typeName === 'ActionResponseMessageOutput') setIsWarnTextHidden(false)

    return
  }

  const onChangeUserDataHandle = useCallback((userData: UserPersonalDataProps) => {
    tenantFormDataRef.current.firstName = userData.firstName
    tenantFormDataRef.current.lastName = userData.lastName
    tenantFormDataRef.current.email = userData.email
    tenantFormDataRef.current.phoneNumber = userData.phoneNumber
    tenantFormDataRef.current.phoneNumber2 = userData.secondaryPhoneNumber
    tenantFormDataRef.current.documentId = userData.documentId
    tenantFormDataRef.current.nationality = userData.nationality
    tenantFormDataRef.current.birthDate = userData.birthDate
    tenantFormDataRef.current.genderOption = userData.genderOption
  }, [])

  const onChangeAddressHandle = useCallback((addressData: AddressProps) => {
    tenantFormDataRef.current.street = addressData.street
    tenantFormDataRef.current.buildingNumber = addressData.buildingNumber
    tenantFormDataRef.current.addressLine = addressData.addressLine
    tenantFormDataRef.current.country = addressData.country
    tenantFormDataRef.current.state = addressData.state
    tenantFormDataRef.current.city = addressData.city
    tenantFormDataRef.current.zipCode = addressData.zipCode
  }, [])

  const onChangeBankDataHandle = useCallback((bankData: BankDataProps) => {
    tenantFormDataRef.current.bankAccountName = bankData.name
    tenantFormDataRef.current.bankAccountHolder = bankData.accountHolderName
    tenantFormDataRef.current.bankAccountNumber = bankData.accountNumber
    tenantFormDataRef.current.bankAccountEmail = bankData.email
    tenantFormDataRef.current.bankAccountPhone = bankData.phone
  }, [])

  const onChangeBillingDataHandle = useCallback((billingData: BillingDataProps) => {
    tenantFormDataRef.current.billingStreet = billingData.street
    tenantFormDataRef.current.billingBuildingNumber = billingData.buildingNumber
    tenantFormDataRef.current.billingAddressLine = billingData.addressLine
    tenantFormDataRef.current.billingCountry = billingData.country
    tenantFormDataRef.current.billingState = billingData.state
    tenantFormDataRef.current.billingCity = billingData.city
    tenantFormDataRef.current.billingZipCode = billingData.zipCode
    tenantFormDataRef.current.billingFirstName = billingData.firstName
    tenantFormDataRef.current.billingLastName = billingData.lastName
  }, [])

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    switch (activity) {
      case 'creating':
        createTenant({ variables: {
          firstName: tenantFormDataRef.current.firstName || '',
          lastName: tenantFormDataRef.current.lastName || '',
          phoneNumber: tenantFormDataRef.current.phoneNumber || '',
          phoneNumber2: tenantFormDataRef.current.phoneNumber2 || '',
          email: tenantFormDataRef.current.email || '',
          documentId: tenantFormDataRef.current.documentId || '',
          nationality: tenantFormDataRef.current.nationality || '',
          genderOption: tenantFormDataRef.current.genderOption || UserGenderTypeOption.Other,
          birthDate: tenantFormDataRef.current.birthDate || '',
          street: tenantFormDataRef.current.street || '',
          buildingNumber: tenantFormDataRef.current.buildingNumber || '',
          addressLine: tenantFormDataRef.current.addressLine || '',
          city: tenantFormDataRef.current.city || '',
          state: tenantFormDataRef.current.state || '',
          country: tenantFormDataRef.current.country || '',
          zipCode: tenantFormDataRef.current.zipCode || '',
          bankAccountName: tenantFormDataRef.current.bankAccountName || undefined,
          bankAccountHolder: tenantFormDataRef.current.bankAccountHolder || undefined,
          bankAccountNumber: tenantFormDataRef.current.bankAccountNumber || undefined,
          bankAccountEmail: tenantFormDataRef.current.bankAccountEmail || undefined,
          bankAccountPhone: tenantFormDataRef.current.bankAccountPhone || undefined,
        }})
          .then(onCreateSuccessHandle)
          .catch(errorHandle)
        break

      case 'updating':
        updateTenant({ variables: {
          id: tenantDetail?.user.id,
          firstName: tenantFormDataRef.current.firstName || undefined,
          lastName: tenantFormDataRef.current.lastName || undefined,
          phoneNumber: tenantFormDataRef.current.phoneNumber || undefined,
          phoneNumber2: tenantFormDataRef.current.phoneNumber2 || undefined,
          email: tenantFormDataRef.current.email || undefined,
          documentId: tenantFormDataRef.current.documentId || undefined,
          nationality: tenantFormDataRef.current.nationality || undefined,
          genderOption: tenantFormDataRef.current.genderOption || undefined ,
          birthDate: tenantFormDataRef.current.birthDate || undefined,
          street: tenantFormDataRef.current.street || undefined,
          buildingNumber: tenantFormDataRef.current.buildingNumber || undefined,
          addressLine: tenantFormDataRef.current.addressLine || undefined,
          city: tenantFormDataRef.current.city || undefined,
          state: tenantFormDataRef.current.state || undefined,
          country: tenantFormDataRef.current.country || undefined,
          zipCode: tenantFormDataRef.current.zipCode || undefined,
          bankAccountName: tenantFormDataRef.current.bankAccountName || undefined,
          bankAccountHolder: tenantFormDataRef.current.bankAccountHolder || undefined,
          bankAccountNumber: tenantFormDataRef.current.bankAccountNumber || undefined,
          bankAccountEmail: tenantFormDataRef.current.bankAccountEmail || undefined,
          bankAccountPhone: tenantFormDataRef.current.bankAccountPhone || undefined,
        }})
        .then(onUpdateSuccessHandle)
        .catch(errorHandle)
        break
    }
  }

  return (
    <StyledForm onSubmit={handleSubmit}>
      <SectionWrapper>
        <span><h2>{texts.user.title}</h2></span>
        <UserPersonalDataFormControlled
          readOnly={readOnly}
          onChange={onChangeUserDataHandle}
          defaultUserData={{
            firstName: tenantDetail?.firstName || '',
            lastName: tenantDetail?.lastName || '',
            email: tenantDetail?.user.email || '',
            phoneNumber: tenantDetail?.phoneNumber || '',
            secondaryPhoneNumber: tenantDetail?.phoneNumber2 || '',
            documentId: tenantDetail?.documentId || '',
            nationality: tenantDetail?.nationality || '',
            birthDate: tenantDetail?.birthDate || '',
            genderOption: tenantDetail?.genderOption || ''
          }}
        />
      </SectionWrapper>
      <br />
      <SectionWrapper>
        <span><h2>{texts.address.title}</h2></span>
        <AddressFormControlled
          readOnly={readOnly}
          onChange={onChangeAddressHandle}
          defaultAddress={{
            street: tenantDetail?.address.street ?? '',
            buildingNumber: tenantDetail?.address.buildingNumber ?? '',
            addressLine: tenantDetail?.address.addressLine ?? '',
            country: tenantDetail?.address.country?.iso2 ?? '',
            state: tenantDetail?.address.state?.geonamesAdmin1Code ?? '',
            city: tenantDetail?.address.city?.geonamesId ?? '',
            zipCode: tenantDetail?.address.zipCode ?? '',
          }}
        />
      </SectionWrapper>
      <br />
      <SectionWrapper>
        <span><h2>{texts.bank?.title ?? 'Información Bancaria'}</h2></span>
        <BankFormControlled
          readOnly={readOnly}
          onChange={onChangeBankDataHandle}
          defaultData={{
            name: tenantDetail?.bankAccount?.name ?? '',
            accountHolderName: tenantDetail?.bankAccount?.holderName ?? '',
            accountNumber: tenantDetail?.bankAccount?.accountNumber ?? '',
            email: tenantDetail?.bankAccount?.email ?? '',
            phone: tenantDetail?.bankAccount?.phone ?? '',
          }}
        />
      </SectionWrapper>
      <br />
      <SectionWrapper>
        <span><h2>{texts.billing.title}</h2></span>
        <BillingFormControlled
          readOnly={readOnly}
          onChange={onChangeBillingDataHandle}
          defaultData={{
            street: '',
            buildingNumber: '',
            addressLine: '',
            country: '',
            state: '',
            city: '',
            zipCode: '',
            firstName: '',
            lastName: '',
            bic: '',
            wereGoing: '',
            observation: '',
          }}
        />
      </SectionWrapper>
      <RowSpacer height={1} />
      <WarnText hidden={isWarnTextHidden} text={texts.warnText} />
      <RowSpacer height={1} />
      <Row justify="center">
        <Col xs="content">
          <Button secondary onClick={() => navigate(-1)}>{texts.user.cancelButton}</Button>
        </Col>
        <Col xs="content">
          <Button submit isLoading={createTenantIsLoading || updateTenantIsLoading}>{texts.user.confirmButton}</Button>
        </Col>
      </Row>
    </StyledForm>
  )
}